// Engagements API — a person's working relationships, company by company
// (multi-entity workforce Phase 1/3). employee-service, routed by the gateway
// under /api/v1/employees. The server returns ONLY the engagements the caller
// may see (owner decision O-2), so the list and its count are already fenced.
import { gatewayApi } from '@/lib/hrApi';

export type RelationshipType = 'employee' | 'independent_contractor';
export type EngagementStatus = 'draft' | 'pending_approval' | 'active' | 'suspended' | 'ended';

export interface EngagementTerms {
  worker_classification?: string | null;
  contract_type?: string | null;
  work_schedule?: string | null;
  employment_type?: string | null;
  position?: string | null;
  job_title_id?: string | null;
  department_id?: string | null;
  manager_id?: string | null;
  branch_id?: string | null;
  cost_center?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  probation_end_date?: string | null;
}

export interface Engagement {
  id: string;
  employee_id: string;
  legal_entity_id: string;
  legal_entity_name?: string | null;
  relationship_type: RelationshipType;
  engagement_number?: string | null;
  is_primary: boolean;
  status: EngagementStatus;
  status_reason?: string | null;
  start_date: string;
  end_date?: string | null;
  end_reason?: string | null;
  submitted_by?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  version: number;
  terms: EngagementTerms;
}

export interface EngagementTermsRow extends EngagementTerms {
  effective_from: string;
  effective_to?: string | null;
  reason?: string | null;
  created_by?: string | null;
}

export interface EngagementCompany {
  id: string;
  name: string;
  country?: string;
  base_currency?: string;
}

export interface CreateEngagementInput {
  legal_entity_id: string;
  relationship_type: RelationshipType;
  start_date: string;
  engagement_number?: string;
  terms: EngagementTerms;
  submit?: boolean;
}

const base = '/api/v1/employees';

export const engagementsApi = {
  list: (employeeId: string) =>
    gatewayApi
      .get<{ engagements: Engagement[]; count: number }>(`${base}/${employeeId}/engagements`)
      .then((r) => r.data),
  create: (employeeId: string, body: CreateEngagementInput) =>
    gatewayApi.post<Engagement>(`${base}/${employeeId}/engagements`, body).then((r) => r.data),
  companies: () =>
    gatewayApi.get<{ companies: EngagementCompany[] }>(`${base}/engagements/companies`).then((r) => r.data.companies),
  history: (id: string) =>
    gatewayApi.get<{ terms: EngagementTermsRow[] }>(`${base}/engagements/${id}/history`).then((r) => r.data.terms),
  changeTerms: (id: string, changes: EngagementTerms, effectiveFrom: string, reason?: string) =>
    gatewayApi
      .patch<Engagement>(`${base}/engagements/${id}`, { changes, effective_from: effectiveFrom, reason })
      .then((r) => r.data),
  submit: (id: string) => gatewayApi.post<Engagement>(`${base}/engagements/${id}/submit`).then((r) => r.data),
  approve: (id: string) => gatewayApi.post<Engagement>(`${base}/engagements/${id}/approve`).then((r) => r.data),
  reject: (id: string, reason: string) =>
    gatewayApi.post<Engagement>(`${base}/engagements/${id}/reject`, { reason }).then((r) => r.data),
  /** G3: pay from this company stops from `effectiveDate` (first suspended day). */
  suspend: (id: string, reason: string, effectiveDate?: string) =>
    gatewayApi
      .post<Engagement>(`${base}/engagements/${id}/suspend`, { reason, ...(effectiveDate ? { effective_date: effectiveDate } : {}) })
      .then((r) => r.data),
  /** G3: pay resumes from `effectiveDate` (first day paid again). */
  resume: (id: string, effectiveDate?: string) =>
    gatewayApi
      .post<Engagement>(`${base}/engagements/${id}/resume`, effectiveDate ? { effective_date: effectiveDate } : {})
      .then((r) => r.data),
  end: (id: string, endDate: string, reason: string) =>
    gatewayApi.post<Engagement>(`${base}/engagements/${id}/end`, { end_date: endDate, reason }).then((r) => r.data),
  /** O-6: confirm a SECOND employment's tax treatment (payroll subject = the engagement id). */
  /** G4: outstanding repayments of an ended employment, and where they can move. */
  repaymentMovePreview: (personId: string) =>
    gatewayApi.get<RepaymentMovePreview>(`/api/v1/payroll/employees/${personId}/repayment-move`).then((r) => r.data),
  moveRepayments: (personId: string, targetEmployeeId: string, reason: string) =>
    gatewayApi
      .post(`/api/v1/payroll/employees/${personId}/repayment-move`, { target_employee_id: targetEmployeeId, reason })
      .then((r) => r.data),
  /**
   * ME7 same-company pair (employee + independent contractor of ONE company):
   * payroll's current "Confirm tax treatment" findings for the PERSON, within
   * the caller's compensation reach (payroll-service #37). Ended pairs keep a
   * finding for their historical overlap.
   */
  sameEntityTaxFindings: (personId: string) =>
    gatewayApi
      .get<SameEntityTaxFindings>(`/api/v1/payroll/employees/${personId}/same-entity-tax`)
      .then((r) => r.data),
  /** Confirm ONE finding's generation (as payroll returned it); 400 note_required, 409 stale_generation / provisional. */
  confirmSameEntityTax: (personId: string, f: SameEntityTaxFinding, note: string) =>
    gatewayApi
      .post(`/api/v1/payroll/employees/${personId}/same-entity-tax/confirm`, {
        legal_entity_id: f.legal_entity_id,
        employee_engagement_id: f.employee_engagement_id,
        contractor_engagement_id: f.contractor_engagement_id,
        generation: f.generation,
        note,
      })
      .then((r) => r.data),
  confirmSecondEmploymentTax: (engagementId: string, note: string) =>
    gatewayApi
      .post(`/api/v1/payroll/employees/${engagementId}/second-employment-tax/confirm`, { note })
      .then((r) => r.data),
};

/** One payroll finding (payroll-service #37, `payroll.same_entity_tax_findings`). */
export interface SameEntityTaxFinding {
  id: string;
  person_id: string;
  legal_entity_id: string;
  employee_engagement_id: string;
  contractor_engagement_id: string;
  /** Opaque; a changed pair (membership, dates, lifecycle — e.g. ended) is a new generation. */
  generation: string;
  /** One side is still a draft / awaiting approval: it cannot be confirmed yet. */
  provisional: boolean;
  overlap_from?: string | null;
  overlap_to?: string | null;
  status: 'open' | 'confirmed';
  confirmed_by?: string | null;
  confirmed_at?: string | null;
  note?: string | null;
  first_seen_at: string;
}

export interface SameEntityTaxFindings {
  person_id: string;
  findings: SameEntityTaxFinding[];
}

export interface RepaymentMovePreview {
  from: { employee_id: string; legal_entity_id: string | null; company_name: string; status: string };
  movable: boolean;
  loans: { id: string; type: string; balance: string; installment: string; currency: string; status: string }[];
  credit_installments: { credit_ref: string; due_period: string; amount_due: string; currency: string }[];
  targets: { employee_id: string; legal_entity_id: string | null; company_name: string }[];
}
